import React, { useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import { addTimeGridMetrics, DEFAULT_SCHEDULE_GRID_TIME_LABELS, getScheduleGridClickTimeRange } from './utils/scheduleGridUtils';

import '../../css/magnetboard.css';

export default function ScheduleGrid({
	days = [],
	getDayKey = (day) => `${day}`,
	getDayLabel = (day) => `Day ${day}`,
	getDayItems = () => [],
	getDayFloatingItems = () => [],
	getDayOverlays = () => [],
	renderItem = () => null,
	renderFloatingItem = null,
	renderOverlay = null,
	renderDayHeaderExtra = null,
	renderDayFooter = null,
	onDayBodyClick = null,
	onAddItem = null,
	addItemLabel = 'Add Item',
	addItemClassName = '',
	className = '',
	timeLabels = DEFAULT_SCHEDULE_GRID_TIME_LABELS
}) {
	const [pendingClickFeedback, setPendingClickFeedback] = useState(null);
	const feedbackTimeoutRef = useRef(null);

	useEffect(() => () => {
		if(feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
	}, []);

	return (
		<div className={`mb-elem-week-schedule-container ${className}`.trim()}>
			<div className='mb-elem-week-schedule-time-container'>
				{timeLabels.map((timeLabel) => (
					<div className='mb-elem-time-delimiter' key={timeLabel}>
						<div>{timeLabel}</div>
					</div>
				))}
			</div>
			<div className='mb-elem-week-schedule-days-container'>
					{days.map((day, dayIndex) => {
						const dayKey = getDayKey(day);
						const dayLabel = getDayLabel(day);
						const dayItems = getDayItems(day) || [];
						const dayFloatingItems = getDayFloatingItems(day) || [];
						const dayOverlays = getDayOverlays(day) || [];
						const loggedPositions = {};

					return (
						<div className='mb-elem-week-day-container' key={dayKey}>
							<h3 className='mb-elem-week-day-header'>{dayLabel}</h3>
								{renderDayHeaderExtra && renderDayHeaderExtra(day, { dayKey, dayLabel, dayIndex, isLastDay: dayIndex === days.length - 1 })}
								<div
									className={`mb-elem-week-day-block-parent-container ${onDayBodyClick ? 'mb-elem-week-day-block-parent-container-clickable' : ''}`.trim()}
									onClick={(e) => {
										if(!onDayBodyClick) return;
										if(e.target.closest('.mb-elem-block') || e.target.closest('.mb-add-new-section') || e.target.closest('.schedule-grid-item-menu-container') || e.target.closest('.schedule-grid-item-menu-toggle')) return;
										const gridElement = e.currentTarget.querySelector('.mb-elem-week-day-grid');
										let clickTimeRange = null;

										if(gridElement)
										{
											const gridRect = gridElement.getBoundingClientRect();
											const clickWithinGridY = e.clientY - gridRect.top;
											if(e.clientY >= gridRect.top && e.clientY <= gridRect.bottom)
											{
												clickTimeRange = getScheduleGridClickTimeRange({
													relativeY: clickWithinGridY,
													gridHeight: gridRect.height
												});
											}
										}

										if(clickTimeRange)
										{
											const [feedbackItem] = addTimeGridMetrics([clickTimeRange], 'feedback_length');
											setPendingClickFeedback({
												dayKey,
												rowStart: feedbackItem.row_start,
												rowEnd: feedbackItem.row_end
											});

											if(feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
											feedbackTimeoutRef.current = setTimeout(() => {
												setPendingClickFeedback(null);
											}, 900);
										}

										onDayBodyClick(day, { dayKey, dayLabel, dayIndex, dayItems, dayFloatingItems, dayOverlays, clickTimeRange });
									}}
								>
									<div className='mb-elem-week-day-grid'>
										{pendingClickFeedback && pendingClickFeedback.dayKey === dayKey && (
											<div
												className='schedule-grid-click-feedback'
												style={{
													gridColumn: '1 / -1',
													gridRowStart: pendingClickFeedback.rowStart,
													gridRowEnd: pendingClickFeedback.rowEnd
												}}
											/>
										)}
										{renderOverlay && dayOverlays.map((overlay, overlayIndex) => {
											const renderedOverlay = renderOverlay(overlay, { day, dayKey, dayLabel, dayIndex, overlayIndex, dayItems, dayFloatingItems, dayOverlays, loggedPositions });
											if(React.isValidElement(renderedOverlay)) return React.cloneElement(renderedOverlay, { key: renderedOverlay.key || `${dayKey}-overlay-${overlayIndex}` });
											return renderedOverlay;
										})}
										{dayItems.map((item, itemIndex) => {
											const renderedItem = renderItem(item, { day, dayItems, dayFloatingItems, dayKey, itemIndex, loggedPositions });
											if(React.isValidElement(renderedItem)) return React.cloneElement(renderedItem, { key: renderedItem.key || `${dayKey}-item-${itemIndex}` });
											return renderedItem;
										})}
										{renderFloatingItem && dayFloatingItems.map((item, itemIndex) => {
											const renderedItem = renderFloatingItem(item, { day, dayItems, dayFloatingItems, dayKey, itemIndex, loggedPositions });
											if(React.isValidElement(renderedItem)) return React.cloneElement(renderedItem, { key: renderedItem.key || `${dayKey}-floating-${itemIndex}` });
											return renderedItem;
										})}
									</div>
								{renderDayFooter ? renderDayFooter(day, { dayKey, dayLabel, dayIndex, dayItems }) : onAddItem && (
									<div className='mb-add-new-section'>
										<div className='mb-elem-add-block-container blue-link' onClick={() => onAddItem(day)}>
											<FontAwesomeIcon icon={faPlusCircle} />
											<div className={addItemClassName}>{addItemLabel}</div>
										</div>
									</div>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
